import React, { useCallback, useEffect, useRef, useState } from "react";
import { PanelHelp } from "./QuickStart";
import { Bell, BellOff, Clock, Eraser } from "lucide-react";
import { toast } from "sonner";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { playPostBell } from "../lib/postBell";

const MUTE_KEY = "raceHandicap.postBellMuted.v1";

const monoStyle = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
};

const fmtMtp = (m) => {
  if (m == null) return "—";
  if (m <= 0) return "POST";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
};

// MTP-driven color ramp — mirrors a race-form status board.
const statusColors = (mtp, status) => {
  if (status === "unknown" || mtp == null) {
    return {
      bg: "#1c1917",
      border: "#44403c",
      accent: "#78716c",
      ring: null,
    };
  }
  if (status === "post" || mtp <= 0) {
    return {
      bg: "#450a0a",
      border: "#7f1d1d",
      accent: "#fca5a5",
      ring: "#fca5a5",
    };
  }
  if (mtp <= 2) {
    // Imminent — flashing red.
    return {
      bg: "#7f1d1d",
      border: "#ef4444",
      accent: "#fef2f2",
      ring: "#ef4444",
    };
  }
  if (mtp <= 10) {
    return {
      bg: "#422006",
      border: "#f59e0b",
      accent: "#fbbf24",
      ring: "#fbbf24",
    };
  }
  if (mtp <= 30) {
    return {
      bg: "#1a2e05",
      border: "#65a30d",
      accent: "#a3e635",
      ring: null,
    };
  }
  return {
    bg: "#0a0a0a",
    border: "#44403c",
    accent: "#a8a29e",
    ring: null,
  };
};

export const MtpBanner = ({ apiBase, onPickTrack, activeSlug, onClearAll }) => {
  const [rows, setRows] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem(MUTE_KEY) === "1";
    } catch {
      return false;
    }
  });
  // Set of slugs we've already rung for in their CURRENT imminent
  // window. A slug stays in the set until it leaves the ≤2m zone, so
  // the bell fires exactly once per race crossing the threshold.
  const notifiedRef = useRef(new Set());
  // Track whether we've completed the first feed load — we never want
  // to ding retroactively for tracks that were already imminent when
  // the page opened.
  const primedRef = useRef(false);
  const mutedRef = useRef(muted);
  useEffect(() => {
    mutedRef.current = muted;
    try {
      localStorage.setItem(MUTE_KEY, muted ? "1" : "0");
    } catch {
      /* ignore quota */
    }
  }, [muted]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase}/mtp-feed`);
      if (!resp.ok) return;
      const data = await resp.json();
      const tracks = Array.isArray(data.tracks) ? data.tracks : [];
      setRows(tracks);
      setLastRefresh(Date.now());
      const imminentNow = new Set();
      for (const t of tracks) {
        const mtp = t.mtp_minutes;
        if (
          t.slug &&
          mtp != null &&
          mtp > 0 &&
          (mtp <= 2 || t.status === "imminent")
        ) {
          imminentNow.add(t.slug);
        }
      }
      if (primedRef.current) {
        const newlyImminent = [...imminentNow].filter(
          (s) => !notifiedRef.current.has(s)
        );
        if (newlyImminent.length > 0 && !mutedRef.current) {
          playPostBell();
          const names = tracks
            .filter((t) => newlyImminent.includes(t.slug))
            .map((t) => `${t.name} R${t.race ?? "?"}`);
          toast.message(`2 min to post — ${names.join(" · ")}`, {
            duration: 6000,
          });
        }
      }
      // Keep only still-imminent slugs so a race that leaves the
      // window can re-trigger if it ever came back.
      notifiedRef.current = imminentNow;
      primedRef.current = true;
    } catch {
      /* silent — banner is a passive display */
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  // First paint — immediate fetch.
  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // 30s refresh, pauses when tab hidden.
  useAutoRefresh(fetchFeed, { enabled: true, intervalMs: 30000 });

  // Partition: active (upcoming / imminent / post) vs closed/unknown.
  const activeRows = rows.filter(
    (r) => r.status && r.status !== "unknown"
  );
  const closedRows = rows.filter(
    (r) => !r.status || r.status === "unknown"
  );
  const visible = showClosed ? rows : activeRows;

  return (
    <div
      className="w-full border-b border-stone-800"
      style={{
        background:
          "linear-gradient(90deg, #0a0a0a 0%, #141414 50%, #0a0a0a 100%)",
      }}
      data-testid="mtp-banner"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-10 py-2.5 flex flex-wrap md:flex-nowrap items-center gap-x-3 gap-y-2">
        <div
          className="flex items-center gap-1.5 shrink-0 mr-auto md:mr-0"
          style={monoStyle}
        >
          <span
            className="inline-flex h-2 w-2 rounded-full bg-[#84cc16] animate-pulse"
            aria-hidden="true"
          />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#84cc16] font-bold">
            Live Board
          </span>
          <Clock size={10} className="text-stone-500" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            MTP
          </span>
          <PanelHelp step={1} label="the Live Board" />
          {!activeSlug && (
            <span
              className="hidden md:inline ml-3 text-[10px] uppercase tracking-[0.2em] text-[#fde047] animate-pulse"
              data-testid="pick-a-track-hint"
            >
              ◂ Pick a track to begin
            </span>
          )}
        </div>
        <div
          className="order-last md:order-none basis-full md:basis-auto min-w-0 flex-1 flex flex-wrap md:flex-nowrap items-center gap-2 md:overflow-x-auto scrollbar-thin pb-0.5"
          data-testid="mtp-track-row"
          style={{ scrollbarWidth: "thin" }}
        >
          {visible.length === 0 && !loading && (
            <span
              className="text-[11px] text-stone-500 italic"
              style={monoStyle}
            >
              No active cards right now — all tracks finished for the day.
            </span>
          )}
          {visible.map((t) => {
            const col = statusColors(t.mtp_minutes, t.status);
            const isPost = t.status === "post" || (t.mtp_minutes != null && t.mtp_minutes <= 0);
            const isImminent = t.status === "imminent";
            const isActive = activeSlug && activeSlug === t.slug;
            return (
              <button
                key={t.slug}
                type="button"
                onClick={() => onPickTrack?.(t)}
                className={`group shrink-0 inline-flex items-center gap-2 px-3 h-8 rounded border transition-all ${
                  isActive ? "ring-2 ring-[#84cc16]" : ""
                }`}
                style={{
                  background: isActive ? "#1a2e05" : col.bg,
                  borderColor: isActive ? "#84cc16" : col.border,
                  boxShadow: isActive
                    ? "0 0 0 1px #84cc16, 0 0 14px #84cc1655"
                    : col.ring
                    ? `0 0 0 1px ${col.ring}33, 0 0 10px ${col.ring}22`
                    : "none",
                  ...monoStyle,
                }}
                title={`${t.name} · Race ${t.race ?? "—"} · ${
                  t.post_time ?? "no post time"
                } — click to select & handicap`}
                data-testid={`mtp-chip-${t.slug}`}
              >
                <span
                  className="text-[10px] uppercase tracking-[0.18em] font-bold"
                  style={{ color: col.accent }}
                >
                  {t.name}
                </span>
                {t.race != null && (
                  <span
                    className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded text-[10px] font-bold"
                    style={{
                      background: "#0a0a0a",
                      color: "#f5ecd6",
                      border: `1px solid ${col.border}`,
                    }}
                  >
                    R{t.race}
                  </span>
                )}
                <span
                  className={`text-[11px] font-bold tracking-[0.1em] ${
                    isImminent ? "animate-pulse" : ""
                  }`}
                  style={{ color: col.accent }}
                >
                  {isPost ? "POST" : fmtMtp(t.mtp_minutes)}
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            // Toggling via a direct click counts as the required user
            // gesture to unlock the AudioContext — ding once when
            // enabling so the user hears the sample.
            setMuted((m) => {
              const next = !m;
              if (!next) {
                try {
                  playPostBell();
                } catch {
                  /* ignore */
                }
              }
              return next;
            });
          }}
          className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 h-7 rounded border transition ${
            muted
              ? "text-stone-500 border-stone-800 hover:text-stone-200 hover:border-stone-600"
              : "text-[#84cc16] border-[#365314] hover:bg-[#1a2e05]"
          }`}
          style={monoStyle}
          data-testid="mtp-bell-toggle"
          title={
            muted
              ? "Post bell OFF — click to enable (dings when any track hits ≤2m)"
              : "Post bell ON — click to mute"
          }
        >
          {muted ? <BellOff size={11} /> : <Bell size={11} />}
          <span className="hidden sm:inline">
            {muted ? "Bell off" : "Bell on"}
          </span>
        </button>
        {activeSlug && (
          <button
            type="button"
            onClick={() => onClearAll?.()}
            className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#fca5a5] border border-[#7f1d1d] hover:bg-[#7f1d1d] hover:text-white px-2 h-7 rounded transition"
            style={monoStyle}
            data-testid="mtp-clear-all"
            title="Clear the selected track, race board, elims and locks — Ticket History is kept."
          >
            <Eraser size={11} />
            <span className="hidden sm:inline">Clear all</span>
          </button>
        )}
        {closedRows.length > 0 && (
          <button
            type="button"
            onClick={() => setShowClosed((v) => !v)}
            className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-stone-500 hover:text-stone-200 px-2 h-7 rounded border border-stone-800 hover:border-stone-600 transition"
            style={monoStyle}
            data-testid="mtp-toggle-closed"
            title={`Toggle visibility of ${closedRows.length} dark/closed tracks`}
          >
            {showClosed ? "Hide dark" : `+${closedRows.length} dark`}
          </button>
        )}
        {lastRefresh && (
          <span
            className="shrink-0 text-[10px] uppercase tracking-[0.18em] text-stone-600 hidden md:inline"
            style={monoStyle}
            data-testid="mtp-updated"
          >
            {new Date(lastRefresh).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
      </div>
    </div>
  );
};
